import { useEffect, useState } from 'react';
import MessageBanner from '../components/elements/MessageBanner';
import { WarningSquiggleIcon } from '../components/svgs/ExpressonSVGs';
import { GET_CUSTOMER_RIGHTS_DATA_QUERY } from '../integrations/allkort/graphql/gql/CustomerQueries';
import { CustomerRightsDataResponse } from '../integrations/allkort/graphql/interface/Customer';
import { useGetCustomer } from './apolloHooks/useGetCustomer';

/**
 * This hook determines if a message banner should be shown based on the customer's status (e.g. frozen, blocked) and returns the appropriate message and styling.
 * It also handles the logic for showing a close button and remembering if the user has closed the banner during the session.
 * @returns 
 */
export const useMessageBanner = () => {
	const { data: currentCustomerData, loading: currentCustomerLoading } = useGetCustomer<CustomerRightsDataResponse>(
		GET_CUSTOMER_RIGHTS_DATA_QUERY,
		{},
		{ fetchPolicy: 'network-only' }
	);

	const [hasCloseButton, setHasCloseButton] = useState(false);
	const [isBannerClosed, setIsBannerClosed] = useState(() => {
		return sessionStorage.getItem('messageBannerClosed') === 'true';
	});

	useEffect(() => {
		const wasClosed = sessionStorage.getItem('messageBannerClosed') === 'true';
		setIsBannerClosed(wasClosed);
	}, []);

	// Define all possible messages - no repeated MessageBanner component
	const messages = {
		frozen: {
			content: (
				<>
					<div className="icon-warning icon-wrapper">
						<WarningSquiggleIcon width={24} height={24} />
					</div>
					<div className="message-wrapper">
						Ditt konto är spärrat. Kontakta kundservice <a href="tel:+4611280000">011-28 00 00</a> för mer information.
					</div>
				</>
			),
			bannerProps: { className: 'error-banner' }
		},
		blocked: {
			content: (
				<>
					<div className="message-wrapper">Ditt konto är begränsat. &nbsp;<a href="/kontakt">Kontakta oss</a>&nbsp; för mer information.</div>
				</>
			),
			bannerProps: { className: 'error-banner' }
		},
		testEnvironment: {
			content: 'Detta är en testmiljö för Qstar MyPages. Inga verkliga transaktioner sker här.',
			bannerProps: {}
		}
	};

	const isFrozen = currentCustomerData?.currentCustomer?.frozen?.is ?? false;
	const customerState = currentCustomerData?.currentCustomer?.state;

	// Update hasCloseButton when frozen status changes
	useEffect(() => {
		if (isFrozen) {
			setHasCloseButton(true);
		}
	}, [isFrozen]);

	// Don't show if loading (check AFTER all hooks are called)
	if (currentCustomerLoading) {
		return null;
	}

	// Determine which message to show based on conditions
	let messageToShow = null;

	if (isFrozen) {
		messageToShow = messages.frozen;
	} else if (customerState) {
		messageToShow = messages.blocked;
	}

	// Don't render if banner is closed
	if (isBannerClosed || !messageToShow) {
		return null;
	}

	return (
		<MessageBanner
			{...messageToShow.bannerProps}
			hasCloseButton={hasCloseButton}
			onCloseBanner={() => {
				sessionStorage.setItem('messageBannerClosed', 'true');
				setIsBannerClosed(true);
			}}
		>
			{messageToShow.content}
		</MessageBanner>
	);
}